import React from "react"
import { JsonEditor } from "./JsonEditor"
import { produce } from "immer"
import axios from "axios"
import { z } from "zod"
import { SearchDropdown } from "./ui-primitives/search-dropdown"
import { ViewerConfig } from "./Viewer"
import { Tabs, TabsList, TabsContent, TabsTrigger } from "./ui-primitives/tabs"
import { Input } from "./ui-primitives/input"
import { useQuery } from "@tanstack/react-query"
import { getAuthHeaders } from "../api/auth"
import { DownloadIcon } from "@radix-ui/react-icons"
import { Button } from "./ui-primitives/button"

const SCHEMA_APPS = z.array(
  z.object({
    id: z.literal(null),
    path: z.string(),
    title: z.string(),
    project: z.object({
      id: z.literal(null),
      path: z.string(),
      title: z.string(),
    }),
  })
)

export type ImportAppJsonConfig = {
  appConfig?: ViewerConfig
  apiURL?: string
  appURL?: string
  importedAt?: Date
  modifiedAt?: Date
}

export const DEFAULT_IMPORT_APP_JSON_CONFIG = {} as const satisfies ImportAppJsonConfig

export function ImportAppJSON(props: {
  name: string
  value: ImportAppJsonConfig
  onChange: (value: ImportAppJsonConfig) => void | Promise<void>
}): React.ReactNode {

  const clearURLs = () => props.onChange(produce(props.value, draft => {
    delete draft.apiURL
    delete draft.appURL
  }))

  const importFromApiURL = async (apiURL: string, appURL?: string) => {
    const res = await axios.get(apiURL)
    props.onChange(produce(props.value, draft => {
      draft.appConfig = res.data as ViewerConfig // TODO V1: parse this with option to disable parsing
      draft.apiURL = apiURL
      draft.importedAt = new Date()

      if (appURL) {
        draft.appURL = appURL
      } else {
        delete draft.appURL
      }
    }))
  }

  return (
    <div className="p-2 border-neutral-500 border-2 rounded-sm flex flex-col gap-2">

      <Tabs defaultValue="import-from-platform">

        <TabsList>
          <TabsTrigger value="import-from-platform">From current platform</TabsTrigger>
          <TabsTrigger value="import-from-url">From URL</TabsTrigger>
        </TabsList>
        <TabsContent value="import-from-platform">
          <ImportFromPlatform
            apiURL={props.value.apiURL}
            import={importFromApiURL}
            reset={clearURLs}
          />
        </TabsContent>
        <TabsContent value="import-from-url">
          <ImportFromURL
            appURL={props.value.appURL}
            import={importFromApiURL}
            reset={clearURLs}
          />
        </TabsContent>
      </Tabs>

      <JsonEditor
        name={props.name + "-appJSON"}
        value={props.value?.appConfig}
        onValueChange={(value) => props.onChange(produce(props.value, draft => {
          draft.appConfig = value as ViewerConfig // TODO V1: parse this with option to disable parsing
          draft.modifiedAt = new Date()
        }))}
      />
      <div className="text-right">{getSourceStatement(props.value)}</div>

    </div>
  )
}

type App = {
  label: string,
  value: string,
}

function ImportFromPlatform(props: {
  apiURL: string | undefined
  import: (apiURL: string, appURL?: string) => Promise<void>
  reset: () => void
}): React.ReactNode {
  const [selectedURL, setSelectedURL] = React.useState<string | null>(props.apiURL ?? null)
  const [search, setSearch] = React.useState<string>('')

  const authHeaders = getAuthHeaders()
  const appsResult = useQuery<App[]>({
    retry: 1,
    retryDelay: 0,
    enabled: Boolean(authHeaders.Authorization),
    queryKey: ["fetch-import-from-platform", authHeaders.Authorization],
    queryFn: async () => {
      const res = await axios.get('/api/list-viewers', {headers: authHeaders})
      const apps = SCHEMA_APPS.parse(res.data)
      return apps.map(app => ({
        label: `${app.project.title}: ${app.title}`,
        value: `/api/projects/${app.project.path}/viewers/${app.path}`,
      }))
    }
  })

  /**
   * Priority:
   * - exact value match (case-sensitive)
   * - exact label match (case-insensitive)
   * - then any keyword match (case-insensitive)
   */
  const filterFn = React.useCallback((value: string, search: string, keywords: string[] = []): number => {
    const label = keywords[0]
    if (value === search) return 1
    if (label && label.toLowerCase() === search.toLowerCase()) return 0.9
    if (keywords.join(' ').toLowerCase().includes(search.toLowerCase())) return 0.5
    return 0
  }, [])

  return (
    <SearchDropdown
      labelConfig={{placeholder: 'select app to import'}}
      highlightWords
      syncSearchWithLabel
      search={search}
      onSearch={setSearch}
      isLoading={appsResult.isLoading}
      options={appsResult.data ?? []}
      onOpenChange={() => appsResult.refetch()}
      getLabelForOptionValue={async (value) => {
        const app = appsResult.data?.find(app => app.value === value)
        if (!app) {
          throw new Error(`no app found for url: ${value}`)
        }
        return app.label
      }}
      value={selectedURL}
      filterFn={filterFn}
      onChange={async (apiURL) => {
        setSelectedURL(apiURL)
        if (apiURL) props.import(apiURL)
        else props.reset()
      }}
    />
  )
}

function ImportFromURL(props: {
  appURL: string | undefined
  import: (apiURL: string, appURL?: string) => void | Promise<void>
  reset: () => void
}): React.ReactNode {
  const [appURL, setURL] = React.useState<string | undefined>(props.appURL)
  const apiURL = React.useMemo(() => {
    if (!appURL) return null

    try {
      const parsed = new URL(appURL)
      const [,, project, app] = parsed.pathname.split('/', 4) // expecting `/projects/project/app`
      return `${parsed.origin}/api/projects/${project}/viewers/${app}`
    } catch(e) {
      return null
    }
  }, [appURL])

  const applyInput = () => {
    if (apiURL && appURL) props.import(apiURL, appURL)
    else props.reset()
  }

  return (
    <div className="flex flex-row w-full gap-2">
      <Input
        placeholder="provide URL and press enter to import"
        value={appURL || ''}
        onChange={e => setURL(e.target.value)}
        onKeyUp={e => {
          if (e.key === 'Enter') applyInput()
        }}
      />
      <Button variant="secondary" className="px-2" 
        title="import config from provided URL"onClick={applyInput}
      ><DownloadIcon /></Button>
    </div>
  )
}

function getSourceStatement({importedAt, modifiedAt}: ImportAppJsonConfig): undefined | string {
  if (importedAt && modifiedAt) {
    if (importedAt > modifiedAt) {
      return `imported at: ${importedAt}`
    } else {
      return `modified at: ${modifiedAt}`
    }
  } else {
    if (importedAt) {
      return `imported at: ${importedAt}`
    }
    if (modifiedAt) {
      return `modified at: ${modifiedAt}`
    }
  }
}
